import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { dashboardThemeCacheKey, normalizeDashboardTheme, resolveDashboardTheme } from '../utils/dashboardTheme';
import '../styles/dashboardTheme.css';

const Context = createContext(null);
export const useDashboardTheme = () => useContext(Context);
const readCache = key => { try { return normalizeDashboardTheme(localStorage.getItem(key)); } catch { return 'light'; } };
const writeCache = (key, value) => { try { localStorage.setItem(key, value); } catch { /* Theme still works without storage. */ } };

export default function DashboardThemeProvider({ user, children }) {
  const cacheKey = dashboardThemeCacheKey(user);
  const [theme, setTheme] = useState(() => readCache(cacheKey));
  const [prefersDark, setPrefersDark] = useState(() => window.matchMedia?.('(prefers-color-scheme: dark)').matches || false);
  const [message, setMessage] = useState('');
  const revision = useRef(0);
  const queue = useRef(Promise.resolve());
  const mounted = useRef(true);
  const activeSaves = useRef(new Set());
  const resolved = resolveDashboardTheme(theme, prefersDark);

  useEffect(() => {
    mounted.current = true;
    const initialRevision = revision.current;
    const controller = new AbortController();
    api.getDashboardTheme({ signal: controller.signal }).then(data => {
      if (!controller.signal.aborted && revision.current === initialRevision) {
        const value = normalizeDashboardTheme(data.theme);
        setTheme(value); writeCache(cacheKey, value);
      }
    }).catch(error => {
      if (!controller.signal.aborted) setMessage('Using this device’s appearance. Account preference could not be loaded.');
    });
    return () => { mounted.current = false; controller.abort(); activeSaves.current.forEach(save => save.abort()); };
  }, [cacheKey]);

  useEffect(() => {
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!media) return;
    const change = event => setPrefersDark(event.matches);
    if (media.addEventListener) media.addEventListener('change', change);
    else media.addListener?.(change);
    return () => { if (media.removeEventListener) media.removeEventListener('change', change); else media.removeListener?.(change); };
  }, []);

  useLayoutEffect(() => {
    const previous = document.body.getAttribute('data-dashboard-theme');
    document.body.setAttribute('data-dashboard-theme', resolved);
    return () => { if (previous === null) document.body.removeAttribute('data-dashboard-theme'); else document.body.setAttribute('data-dashboard-theme', previous); };
  }, [resolved]);

  const selectTheme = value => {
    const next = normalizeDashboardTheme(value);
    revision.current += 1;
    const selectedRevision = revision.current;
    setTheme(next); writeCache(cacheKey, next); setMessage('Saving appearance…');
    // Keep writes ordered so rapid switches cannot save an older selection last.
    queue.current = queue.current.catch(() => {}).then(async () => {
      if (!mounted.current) return;
      const controller = new AbortController();
      activeSaves.current.add(controller);
      try { await api.saveDashboardTheme(next, { signal: controller.signal }); }
      finally { activeSaves.current.delete(controller); }
    }).then(() => {
      if (mounted.current && revision.current === selectedRevision) setMessage('');
    }).catch(() => {
      if (mounted.current && revision.current === selectedRevision) setMessage('Appearance changed on this device. Account save failed; choose again to retry.');
    });
  };
  return <Context.Provider value={{ theme, resolved, selectTheme, message }}>{children}</Context.Provider>;
}
